import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, Users, Calendar, Clock, MapPin, AlertCircle,
  CheckCircle2, XCircle, Loader2, Save, Sparkles, Star
} from 'lucide-react';
import { toast } from 'sonner';
import InstantRewardDialog from '../../components/instructor/InstantRewardDialog';

const attendanceOptions = [
  { value: 'present', label: 'Present', icon: CheckCircle2, color: 'text-accent' },
  { value: 'late', label: 'Late', icon: Clock, color: 'text-warning' },
  { value: 'absent', label: 'Absent', icon: XCircle, color: 'text-destructive' },
  { value: 'excused', label: 'Excused', icon: AlertCircle, color: 'text-muted-foreground' },
];

export default function InstructorSectionPage() {
  const { sectionId } = useParams();
  const { api } = useAuth();
  const [section, setSection] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [sectionId]);

  const fetchData = async () => {
    try {
      const [sectionRes, enrollmentsRes, attendanceRes] = await Promise.all([
        api.get(`/sections/${sectionId}`),
        api.get('/enrollments/', { params: { section_id: sectionId } }),
        api.get(`/attendance/section/${sectionId}`),
      ]);
      setSection(sectionRes.data);
      setEnrollments(enrollmentsRes.data.filter(e => e.status === 'confirmed'));
      
      // Map attendance records
      const attendanceMap = {};
      attendanceRes.data.forEach(record => {
        if (!attendanceMap[record.meeting_date]) {
          attendanceMap[record.meeting_date] = {};
        }
        attendanceMap[record.meeting_date][record.learner_id] = record.status;
      });
      setAttendance(attendanceMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Demo data
      setSection({
        id: sectionId,
        course_title: 'Python Fundamentals',
        location_name: 'Downtown Center',
        start_date: '2025-02-03',
        end_date: '2025-03-21',
        start_time: '4:00 PM',
        end_time: '5:30 PM',
        meeting_days: ['Monday', 'Wednesday'],
        enrolled_count: 12,
        capacity: 15,
      });
      setEnrollments([
        { id: 'e1', learner_id: 'l1', learner_name: 'Alex Smith' },
        { id: 'e2', learner_id: 'l2', learner_name: 'Jordan Lee' },
        { id: 'e3', learner_id: 'l3', learner_name: 'Taylor Chen' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAttendanceChange = (learnerId, status) => {
    setAttendance(prev => ({
      ...prev,
      [selectedDate]: {
        ...prev[selectedDate],
        [learnerId]: status,
      }
    }));
  };

  const saveAttendance = async () => {
    setSaving(true);
    try {
      const dateAttendance = attendance[selectedDate] || {};
      for (const [learnerId, status] of Object.entries(dateAttendance)) {
        await api.post(`/attendance/section/${sectionId}/mark`, {
          learner_id: learnerId,
          meeting_date: selectedDate,
          status: status,
        });
      }
      toast.success('Attendance saved');
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!section) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Section not found</p>
        <Link to="/instructor">
          <Button className="mt-4">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  const dateAttendance = attendance[selectedDate] || {};

  return (
    <div className="space-y-6" data-testid="section-page">
      {/* Back Button */}
      <Link to="/instructor" className="inline-flex items-center text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Dashboard
      </Link>

      {/* Section Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-heading text-2xl font-bold">{section.course_title}</h1>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {section.location_name}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {section.start_time} - {section.end_time}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {Array.isArray(section.meeting_days) ? section.meeting_days.join(', ') : (section.meeting_days || 'TBD')}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {enrollments.length}/{section.capacity}
            </span>
          </div>
        </div>
        
        {/* Instant Reward Button */}
        <Button 
          onClick={() => setRewardDialogOpen(true)}
          className="bg-yellow-500 hover:bg-yellow-600 text-white"
          data-testid="instant-reward-btn"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Award Points
        </Button>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="attendance">
        <TabsList>
          <TabsTrigger value="attendance" data-testid="attendance-tab">Attendance</TabsTrigger>
          <TabsTrigger value="roster" data-testid="roster-tab">Roster</TabsTrigger>
        </TabsList>

        {/* Attendance Tab */}
        <TabsContent value="attendance" className="mt-6">
          <Card className="card-dashboard">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Mark Attendance</CardTitle>
              <div className="flex items-center gap-4">
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-auto"
                  data-testid="attendance-date"
                />
                <Button onClick={saveAttendance} disabled={saving} data-testid="save-attendance-btn">
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {enrollments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No students enrolled in this section.
                </p>
              ) : (
                <div className="space-y-4">
                  {enrollments.map((enrollment, index) => (
                    <div
                      key={enrollment.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                      data-testid={`attendance-row-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-bold text-sm">
                            {enrollment.learner_name?.split(' ').map(n => n[0]).join('') || 'S'}
                          </span>
                        </div>
                        <span className="font-medium">
                          {enrollment.learner_name || `Student ${index + 1}`}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        {attendanceOptions.map(option => {
                          const Icon = option.icon;
                          const isSelected = dateAttendance[enrollment.learner_id] === option.value;
                          return (
                            <Button
                              key={option.value}
                              variant={isSelected ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => handleAttendanceChange(enrollment.learner_id, option.value)}
                              className={isSelected ? '' : option.color}
                              data-testid={`attendance-${index}-${option.value}`}
                            >
                              <Icon className="h-4 w-4 mr-1" />
                              {option.label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roster Tab */}
        <TabsContent value="roster" className="mt-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle>Class Roster</CardTitle>
            </CardHeader>
            <CardContent>
              {enrollments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No students enrolled in this section.
                </p>
              ) : (
                <div className="space-y-4">
                  {enrollments.map((enrollment, index) => (
                    <div
                      key={enrollment.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                      data-testid={`roster-row-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-bold text-sm">
                            {enrollment.learner_name?.split(' ').map(n => n[0]).join('') || 'S'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">
                            {enrollment.learner_name || `Student ${index + 1}`}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Enrolled
                          </p>
                        </div>
                      </div>
                      <Badge variant="secondary">Active</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Instant Reward Dialog */}
      <InstantRewardDialog
        open={rewardDialogOpen}
        onOpenChange={setRewardDialogOpen}
        sectionId={sectionId}
        sessionNumber={1}
        students={enrollments.map(e => ({
          id: e.learner_id,
          first_name: e.learner_name?.split(' ')[0] || 'Student',
          last_name: e.learner_name?.split(' ').slice(1).join(' ') || ''
        }))}
        onRewardGiven={(data) => {
          console.log('Reward given:', data);
        }}
      />
    </div>
  );
}
